import { apiFetch } from '@/platform/lib/api';

export type TicketType = 'work_request' | 'incident' | 'problem' | 'change';
export type TicketSource = 'feedback' | 'admin' | 'reporter' | 'import';

export type Ticket = {
  id: string;
  type: TicketType;
  stage: string;
  title: string;
  description: string;
  source: TicketSource;
  originTenantId: string;
  originDomain: string;
  originUserId: string;
  reporterUid?: string;
  reporterDisplay: string;
  reporterEmail?: string;
  feedbackCategory?: string;
  category?: string;
  priority?: string;
  impact?: string;
  urgency?: string;
  linkedProblemId?: string;
  assigneeUid?: string;
  agentResponse?: string;
  createdAt: string;
  updatedAt: string;
  version: number;
};

export type TicketEvent = {
  id: string;
  ticketId: string;
  eventType: string;
  actorUid: string;
  body?: string;
  payload?: Record<string, unknown>;
  createdAt: string;
};

export type TicketMessage = {
  id: string;
  ticketId: string;
  authorUid: string;
  body: string;
  internal: boolean;
  createdAt: string;
};

export type KBArticle = {
  id: string;
  title: string;
  body: string;
  published: boolean;
  authorUid: string;
  createdAt: string;
  updatedAt: string;
};

export type PipelinePrefs = {
  stages: string[];
  categories?: string[];
  priorities?: string[];
  impacts?: string[];
  urgencies?: string[];
  sla?: Record<string, string>;
};

export type ITSMMe = {
  email?: string;
  isReporter: boolean;
  isAgent: boolean;
  portalAdmin: boolean;
  canAccess: boolean;
};

export type AgentRosterEntry = {
  uid: string;
  email?: string;
  displayName?: string;
  portalAdmin: boolean;
  workspaceGroupIds?: string[];
};

export function fetchITSMMe() {
  return apiFetch<ITSMMe>('/servicedesk/me');
}

export function listServicedeskAgents() {
  return apiFetch<{ agents: AgentRosterEntry[] }>('/admin/servicedesk/agents');
}

export function listServicedeskTickets(params?: {
  stage?: string;
  type?: TicketType;
  originTenantId?: string;
  assigneeUid?: string;
  limit?: number;
}) {
  const q = new URLSearchParams();
  if (params?.stage) q.set('stage', params.stage);
  if (params?.type) q.set('type', params.type);
  if (params?.originTenantId) q.set('originTenantId', params.originTenantId);
  if (params?.assigneeUid) q.set('assigneeUid', params.assigneeUid);
  if (params?.limit) q.set('limit', String(params.limit));
  const qs = q.toString();
  return apiFetch<{ tickets: Ticket[] }>(`/admin/servicedesk/tickets${qs ? `?${qs}` : ''}`);
}

export function createAgentTicket(body: {
  type: TicketType;
  title: string;
  description: string;
  category?: string;
  priority?: string;
  impact?: string;
  urgency?: string;
  assigneeUid?: string;
  reporterEmail?: string;
  reporterDisplay?: string;
}) {
  return apiFetch<Ticket>('/admin/servicedesk/tickets', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function getServicedeskTicket(id: string) {
  return apiFetch<Ticket>(`/admin/servicedesk/tickets/${id}`);
}

export function updateServicedeskTicket(
  id: string,
  body: Partial<{
    stage: string;
    title: string;
    description: string;
    assigneeUid: string;
    agentResponse: string;
    category: string;
    priority: string;
    impact: string;
    urgency: string;
    linkedProblemId: string;
  }>,
) {
  return apiFetch<Ticket>(`/admin/servicedesk/tickets/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function listServicedeskTicketEvents(id: string) {
  return apiFetch<{ events: TicketEvent[] }>(`/admin/servicedesk/tickets/${id}/events`);
}

export function listTicketMessages(id: string, agent = true) {
  const path = agent
    ? `/admin/servicedesk/tickets/${id}/messages`
    : `/servicedesk/me/tickets/${id}/messages`;
  return apiFetch<{ messages: TicketMessage[] }>(path);
}

export function postTicketMessage(id: string, body: string, internal = false, agent = true) {
  const path = agent
    ? `/admin/servicedesk/tickets/${id}/messages`
    : `/servicedesk/me/tickets/${id}/messages`;
  return apiFetch<TicketMessage>(path, {
    method: 'POST',
    body: JSON.stringify({ body, internal }),
  });
}

export function fetchServicedeskPipeline(agent = true) {
  return apiFetch<PipelinePrefs>(agent ? '/admin/servicedesk/pipeline' : '/servicedesk/me/pipeline');
}

export function saveServicedeskPipeline(prefs: PipelinePrefs) {
  return apiFetch<PipelinePrefs>('/admin/servicedesk/pipeline', {
    method: 'PUT',
    body: JSON.stringify(prefs),
  });
}

export function listMyTickets() {
  return apiFetch<{ tickets: Ticket[] }>('/servicedesk/me/tickets');
}

export function createMyTicket(body: {
  type: TicketType;
  title: string;
  description: string;
  category?: string;
  priority?: string;
  impact?: string;
  urgency?: string;
}) {
  return apiFetch<Ticket>('/servicedesk/me/tickets', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function listKB(agent = false) {
  return apiFetch<{ articles: KBArticle[] }>(agent ? '/admin/servicedesk/kb' : '/servicedesk/me/kb');
}

export function saveKBArticle(body: { title: string; body: string; published: boolean }, id?: string) {
  if (id) {
    return apiFetch<KBArticle>(`/admin/servicedesk/kb/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }
  return apiFetch<KBArticle>('/admin/servicedesk/kb', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export const DEFAULT_STAGES = ['intake', 'triage', 'in_progress', 'done', 'closed'] as const;

export const STAGE_LABELS: Record<string, string> = {
  intake: 'Intake',
  triage: 'Triage',
  in_progress: 'In progress',
  done: 'Done',
  closed: 'Closed',
};

export const TYPE_LABELS: Record<TicketType, string> = {
  work_request: 'Work requests',
  incident: 'Incidents',
  problem: 'Problems',
  change: 'Changes',
};
